import {
  CLIENT_MESSAGES,
  type BlockedUser,
  type ConversationSummary,
  type DirectMessageEvent,
  type DmSend,
  type RosterEntry,
} from '@veriverse/shared';
import { useSessionStore } from '../state/session-store.js';
import { api } from './api.js';

/** Direct messages (spec §6.7): realtime for sending and delivery, REST for history and blocks. */

const state = () => useSessionStore.getState();

export async function loadConversations(): Promise<void> {
  try {
    state().setConversations(await api<ConversationSummary[]>('/api/dm'));
  } catch {
    /* the badge is a convenience; the next message refreshes it */
  }
}

export async function loadBlocked(): Promise<void> {
  try {
    state().setBlocked(await api<BlockedUser[]>('/api/blocks'));
  } catch {
    /* shown as an empty list; the server still enforces blocks */
  }
}

/** Colleague names for starting new conversations. */
export async function loadRoster(): Promise<RosterEntry[]> {
  const roster = await api<RosterEntry[]>('/api/roster');
  state().setNames(Object.fromEntries(roster.map((r) => [r.userId, r.displayName])));
  return roster;
}

/** Shows a conversation: loads its history and marks it read. */
export async function openConversation(userId: string): Promise<void> {
  state().setActiveDm(userId);
  try {
    const history = await api<DirectMessageEvent[]>(`/api/dm/${userId}`);
    state().setThread(userId, history);
    state().markConversationRead(userId);
    await api(`/api/dm/${userId}/read`, { method: 'POST' });
  } catch {
    /* thread stays empty; sending still works */
  }
}

export function sendDm(to: string, body: string): void {
  const room = state().room;
  const text = body.trim();
  if (!room || !text) return;
  state().setDmNotice(undefined);
  const message: DmSend = { to, body: text };
  room.send(CLIENT_MESSAGES.dm, message);
}

/** A DM arrived (or our own was confirmed by the server). */
export function handleIncomingDm(event: DirectMessageEvent): void {
  state().receiveDm(event);
  const { user, dmOpen, activeDm } = state();
  const other = event.senderId === user?.id ? event.recipientId : event.senderId;
  if (event.senderId !== user?.id && dmOpen && activeDm === other) {
    void api(`/api/dm/${other}/read`, { method: 'POST' }).catch(() => undefined);
  }
}

export async function setBlocked(userId: string, blocked: boolean): Promise<void> {
  await api(`/api/blocks/${userId}`, { method: blocked ? 'PUT' : 'DELETE' });
  state().setConversations(
    state().conversations.map((c) => (c.userId === userId ? { ...c, blocked } : c)),
  );
  await loadBlocked();
}
